import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import CustomStepper from 'lib/common/Stepper';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useMemo } from 'react';

import { SimpleCessionDialogProps } from '../../types';
import { useCessionBatchPolling } from './useCessionBatchPolling';

const PollingCessionDialog: React.FC<SimpleCessionDialogProps> = ({
  open,
  setOpen,
  documents,
  bankAccountId,
  funds = [],
  amountToReceiveInput,
  setUpMainPage,
  resellsToApply,
}) => {
  const {
    batchStatus,
    initialLoading,
    executingCessions,
    phase,
    failedStep,
    errorMessage,
    progress,
    tableData,
    activeStep,
    isCompleted,
    handleClose,
    handleFinish,
  } = useCessionBatchPolling({
    open,
    setOpen,
    documents,
    bankAccountId,
    funds,
    amountToReceiveInput,
    setUpMainPage,
    resellsToApply,
  });

  const steps = useMemo(() => {
    const step1Content = (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {phase === 'loading-xmls' && 'Cargando XMLs de documentos...'}
            {phase === 'ready-to-cede' &&
              'XMLs cargados. Listo para ejecutar cesiones.'}
            {phase === 'executing-cessions' && 'Ejecutando cesiones...'}
            {phase === 'completed' && 'Proceso completado'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary">
              {progress.percentage}%
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {progress.processed} de {progress.total} documentos procesados
            {progress.successful > 0 && ` • ${progress.successful} exitosos`}
            {progress.failed > 0 && ` • ${progress.failed} fallidos`}
          </Typography>
        </Box>

        <Box sx={{ mt: 3 }}>
          <EnhancedTable
            dataHeaders={[
              { key: 'folio', label: 'Folio' },
              { key: 'status', label: 'Estado', type: 'status-chip' },
              { key: 'message', label: 'Mensaje' },
            ]}
            data={tableData}
            tableSize="small"
            hideTablePagination={true}
            rowsPerPageDefault={100}
          />
        </Box>

        {failedStep === 0 && errorMessage && (
          <AlertBanner
            severity="error"
            message={errorMessage}
            customIcon={<ErrorIcon />}
            sx={{ mt: 2 }}
          />
        )}
      </Box>
    );

    const step2Content = (
      <Box sx={{ mt: 2 }}>
        {failedStep === 1 && errorMessage ? (
          <AlertBanner
            severity="error"
            message={errorMessage}
            customIcon={<ErrorIcon />}
            sx={{ mb: 2 }}
          />
        ) : (
          <>
            <AlertBanner
              severity="success"
              message="Los XMLs han sido cargados exitosamente. Se están ejecutando las cesiones."
              customIcon={<DoneIcon />}
              sx={{ mb: 2 }}
            />
            {executingCessions && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            )}
          </>
        )}
      </Box>
    );

    const step3Content = (
      <Box sx={{ mt: 2 }}>
        {progress.successful > 0 && (
          <AlertBanner
            severity="success"
            message={`${progress.successful} documento${progress.successful > 1 ? 's' : ''} cedido${progress.successful > 1 ? 's' : ''} exitosamente.`}
            customIcon={<DoneIcon />}
            sx={{ mb: 2 }}
          />
        )}
        {progress.failed > 0 && (
          <AlertBanner
            severity="warning"
            message={`${progress.failed} documento${progress.failed > 1 ? 's' : ''} con error${progress.failed > 1 ? 'es' : ''} al procesar.`}
            customIcon={<WarningIcon />}
            sx={{ mb: 2 }}
          />
        )}
      </Box>
    );

    return [
      {
        label: 'Cargando XMLs',
        description: 'Procesando documentos y cargando XMLs',
        content: step1Content,
        completed:
          (phase === 'ready-to-cede' ||
            phase === 'executing-cessions' ||
            phase === 'completed') &&
          failedStep !== 0,
        error: failedStep === 0,
        errorMessage: failedStep === 0 ? errorMessage || 'Error' : undefined,
      },
      {
        label: 'Ejecutar Cesiones',
        description: 'Confirmar y ejecutar las cesiones de facturas',
        content: step2Content,
        completed: phase === 'completed' && isCompleted && failedStep !== 1,
        disabled:
          (phase !== 'ready-to-cede' &&
            phase !== 'executing-cessions' &&
            phase !== 'completed') ||
          failedStep === 0,
        error: failedStep === 1,
        errorMessage: failedStep === 1 ? errorMessage || 'Error' : undefined,
      },
      {
        label: 'Completado',
        description: 'Cesiones ejecutadas exitosamente',
        content: step3Content,
        completed: false,
        disabled: phase !== 'completed' || !isCompleted,
      },
    ];
  }, [
    phase,
    progress,
    isCompleted,
    tableData,
    executingCessions,
    failedStep,
    errorMessage,
  ]);

  return (
    <Dialog maxWidth="lg" fullWidth open={open}>
      <DialogTitle>
        <Typography variant="h6">Cesión de facturas</Typography>
      </DialogTitle>
      <DialogContent>
        {initialLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
            }}
          >
            <CenteredCircularProgress type="dialog" />
          </div>
        ) : !batchStatus ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <CustomStepper
            steps={steps}
            activeStep={activeStep}
            showNavigationButtons={false}
            maxWidth="100%"
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {failedStep !== null && (
          <CustomButton onClick={handleClose} color="secondary">
            Cerrar
          </CustomButton>
        )}
        {activeStep === 2 && isCompleted && failedStep === null && (
          <CustomButton onClick={handleFinish} color="primary">
            Finalizar
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PollingCessionDialog;
